import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const logout = useCallback(() => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        setUser(null);
        setError(null);
    }, []);

    useEffect(() => {
        const accessToken = localStorage.getItem('access_token');
        const savedUser = localStorage.getItem('user');
        
        if (accessToken && savedUser) {
            setUser(JSON.parse(savedUser));
            
            // Verify token is still valid
            authAPI.getMe()
                .then(res => {
                    setUser(res.data.user || res.data);
                    localStorage.setItem('user', JSON.stringify(res.data.user || res.data));
                    setError(null);
                })
                .catch(() => {
                    logout();
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setLoading(false);
        }
    }, [logout]);

    const login = async (email, password) => {
        setError(null);
        try {
            const res = await authAPI.login({ email, password });
            const { access_token, refresh_token, user: userData } = res.data;
            
            localStorage.setItem('access_token', access_token);
            localStorage.setItem('refresh_token', refresh_token || '');
            localStorage.setItem('user', JSON.stringify(userData));
            
            setUser(userData);
            return userData;
        } catch (err) {
            setError(err.response?.data?.message || 'Login failed');
            throw err;
        }
    };

    const register = async (data) => {
        setError(null);
        try {
            const res = await authAPI.register(data);
            const { access_token, refresh_token, user: userData } = res.data;
            
            localStorage.setItem('access_token', access_token);
            localStorage.setItem('refresh_token', refresh_token || '');
            localStorage.setItem('user', JSON.stringify(userData));
            
            setUser(userData);
            return userData;
        } catch (err) {
            setError(err.response?.data?.message || 'Registration failed');
            throw err;
        }
    };

    const updateUser = (userData) => {
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
    };

    return (
        <AuthContext.Provider value={{ 
            user, 
            loading, 
            error,
            login, 
            register, 
            logout, 
            updateUser
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}